import { Tooltip } from "@mui/material";
import {
  gridFilteredSortedRowIdsSelector,
  gridRowGroupingSanitizedModelSelector,
  gridRowTreeSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import ExpandCollapseButton from "@entrilia/shared/components/ExpandCollapseButton.tsx";
import CollapseAllIcon from "@entrilia/shared/icons/CollapseAllIcon.tsx";
import ExpandAllIcon from "@entrilia/shared/icons/ExpandAllIcon.tsx";
import { getGroupExpansionScope } from "./utils/getGroupExpansionScope.ts";

interface Props {
  field?: string;
  caption?: string;
}

const GroupLevelExpansionButton = ({ field, caption }: Props) => {
  const apiRef = useGridApiContext();
  const groupingModel = useGridSelector(apiRef, gridRowGroupingSanitizedModelSelector);
  const rowTree = useGridSelector(apiRef, gridRowTreeSelector);
  const rowIds = useGridSelector(apiRef, gridFilteredSortedRowIdsSelector);
  const { groupIds, expanded } = getGroupExpansionScope(
    rowTree,
    rowIds,
    field === undefined ? 0 : groupingModel.indexOf(field)
  );
  const label =
    field === undefined
      ? `${expanded ? "Collapse" : "Expand"} all`
      : `${expanded ? "Collapse" : "Expand"} all ${caption ?? field} groups and subgroups`;

  const handleToggle = () => {
    const allGroupIds = Object.values(rowTree).filter((node) => node.type === "group" && node.depth >= 0);
    if (groupIds.length === allGroupIds.length) {
      if (expanded) {
        apiRef.current.collapseAllRows();
      } else {
        apiRef.current.expandAllRows();
      }
      return;
    }
    const changedIds = groupIds.filter((id) => {
      const node = rowTree[id];
      return node?.type === "group" && Boolean(node.childrenExpanded) === expanded;
    });
    const orderedIds = expanded ? [...changedIds].reverse() : changedIds;
    orderedIds.forEach((id) => apiRef.current.setRowChildrenExpansion(id, !expanded));
  };

  return (
    <Tooltip title={label} arrow enterDelay={500} enterNextDelay={500}>
      <ExpandCollapseButton
        expanded={expanded}
        onToggle={handleToggle}
        disabled={groupIds.length === 0}
        aria-label={label}
        ExpandIcon={ExpandAllIcon}
        CollapseIcon={CollapseAllIcon}
      />
    </Tooltip>
  );
};

export default GroupLevelExpansionButton;
